/**
 * AuditPage — Consulter le journal d'audit de sécurité et le registre d'événements (Sprint 8.x)
 *
 * Gated: 'auditor', 'admin', 'root'
 *
 * Fonctionnalités :
 *   - Liste des événements de sécurité (GET /api/v1/audit/security-events)
 *   - Visualisation détaillée par trace_id (GET /api/v1/audit/{trace_id})
 *   - Liste des événements ledger (GET /api/v1/ledger/events)
 *   - Tri et filtres (date, niveau de classification, type d'événement)
 *
 * Style fonctionnel strict (zéro boucle for/while).
 */
import React, { useCallback, useEffect, useState } from 'react';
import { Search, ShieldAlert, FileText, Database, Calendar, User, Eye, ArrowRight } from 'lucide-react';
import { useSDK } from '../context/SDKContext.tsx';

// ── Types ────────────────────────────────────────────────────────────────────

interface SecurityEvent {
  id: string;
  created_at: string;
  audit_event: string;
  outcome: string;
  actor_id?: string;
  actor_email?: string;
  actor_role?: string;
  actor_clearance?: string;
  classification?: string;
  resource?: string;
  trace_id?: string;
  detail?: string;
}

interface LedgerEvent {
  event_id: string;
  event_type: string;
  dossier_id: string;
  timestamp: string;
  actor: Record<string, string>;
  payload: Record<string, any>;
  status: string;
  cost?: number;
  duration_ms?: number;
  trace_id?: string;
}

export default function AuditPage(): React.ReactElement {
  const { sdk, isSimulated } = useSDK();

  // Onglets : 'security' | 'ledger' | 'trace'
  const [activeTab, setActiveTab] = useState<'security' | 'ledger' | 'trace'>('security');

  // Données de base
  const [securityEvents, setSecurityEvents] = useState<SecurityEvent[]>([]);
  const [ledgerEvents, setLedgerEvents] = useState<LedgerEvent[]>([]);
  
  // Recherche de trace
  const [searchTraceId, setSearchTraceId] = useState('');
  const [searchedTraceId, setSearchedTraceId] = useState('');
  const [traceDetail, setTraceDetail] = useState<{ trace_id: string; events: LedgerEvent[] } | null>(null);

  // UI States
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Filtres
  const [filterEvent, setFilterEvent] = useState('');
  const [filterClassification, setFilterClassification] = useState('');

  // ── Chargement Security Events ──────────────────────────────────────────────

  const loadSecurityEvents = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // sdk.audit.listSecurityEvents()
      const { data } = await sdk.audit.listSecurityEvents();
      setSecurityEvents(data || []);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  }, [sdk]);

  // ── Chargement Ledger Events ────────────────────────────────────────────────

  const loadLedgerEvents = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // sdk.audit.listLedgerEvents()
      const { data } = await sdk.audit.listLedgerEvents();
      setLedgerEvents(data || []);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  }, [sdk]);

  // ── Recherche Trace_ID ──────────────────────────────────────────────────────

  const handleSearchTrace = useCallback(async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    if (!searchTraceId.trim()) return;

    setLoading(true);
    setError(null);
    setTraceDetail(null);
    setSearchedTraceId(searchTraceId);

    try {
      const { data } = await sdk.audit.getTrace(searchTraceId.trim());
      setTraceDetail(data);
    } catch (err) {
      setError((err as Error).message || "Aucune trace trouvée pour cet identifiant");
    } finally {
      setLoading(false);
    }
  }, [sdk, searchTraceId]);

  // Effet de chargement initial selon l'onglet
  useEffect(() => {
    if (activeTab === 'security') {
      void loadSecurityEvents();
    } else if (activeTab === 'ledger') {
      void loadLedgerEvents();
    } else if (activeTab === 'trace' && searchedTraceId) {
      void handleSearchTrace();
    }
  }, [activeTab, loadSecurityEvents, loadLedgerEvents, handleSearchTrace, searchedTraceId]);

  // ── Rendu ───────────────────────────────────────────────────────────────────

  // Filtre fonctionnel des événements de sécurité
  const filteredSecEvents = securityEvents.filter((evt) => {
    const matchEvent = filterEvent ? evt.audit_event.toLowerCase().includes(filterEvent.toLowerCase()) : true;
    const matchClass = filterClassification ? evt.classification === filterClassification : true;
    return matchEvent && matchClass;
  });

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Registre d'Audit & Sécurité</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Traçabilité des décisions agentiques, accès clearance et conformité RGPD/AI Act
          </p>
        </div>
        {isSimulated && (
          <span className="px-2 py-1 bg-orange-500/10 text-orange-500 text-xs rounded font-medium border border-orange-500/20">
            Mode Simulation
          </span>
        )}
      </div>

      {/* Onglets */}
      <div className="flex border-b border-border">
        <button
          onClick={() => setActiveTab('security')}
          className={`flex items-center gap-2 px-4 py-2 border-b-2 text-sm font-medium transition-colors ${
            activeTab === 'security'
              ? 'border-primary text-primary'
              : 'border-transparent text-muted-foreground hover:text-foreground'
          }`}
        >
          <ShieldAlert size={16} />
          Sécurité & Clearance
        </button>
        <button
          onClick={() => setActiveTab('ledger')}
          className={`flex items-center gap-2 px-4 py-2 border-b-2 text-sm font-medium transition-colors ${
            activeTab === 'ledger'
              ? 'border-primary text-primary'
              : 'border-transparent text-muted-foreground hover:text-foreground'
          }`}
        >
          <Database size={16} />
          Registre Ledger Events
        </button>
        <button
          onClick={() => setActiveTab('trace')}
          className={`flex items-center gap-2 px-4 py-2 border-b-2 text-sm font-medium transition-colors ${
            activeTab === 'trace'
              ? 'border-primary text-primary'
              : 'border-transparent text-muted-foreground hover:text-foreground'
          }`}
        >
          <Search size={16} />
          Recherche Trace
        </button>
      </div>

      {error && (
        <div className="rounded-md bg-destructive/10 p-4 text-sm text-destructive">
          {error}
        </div>
      )}

      {/* Vue Onglet : Sécurité */}
      {activeTab === 'security' && (
        <div className="space-y-4">
          {/* Outils de filtre */}
          <div className="flex flex-col sm:flex-row gap-4 bg-card p-4 border border-border rounded-lg">
            <div className="flex-1">
              <label className="text-xs text-muted-foreground uppercase tracking-wide block mb-1">
                Filtrer par événement
              </label>
              <input
                type="text"
                value={filterEvent}
                onChange={(e) => setFilterEvent(e.target.value)}
                placeholder="Ex: login, clearance_denied..."
                className="w-full bg-background border border-border rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-primary"
              />
            </div>
            <div className="w-full sm:w-48">
              <label className="text-xs text-muted-foreground uppercase tracking-wide block mb-1">
                Classification
              </label>
              <select
                value={filterClassification}
                onChange={(e) => setFilterClassification(e.target.value)}
                className="w-full bg-background border border-border rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-primary"
              >
                <option value="">Toutes</option>
                <option value="C1">C1 (Public)</option>
                <option value="C2">C2 (Interne)</option>
                <option value="C3">C3 (Sensible)</option>
                <option value="C4">C4 (Critique)</option>
              </select>
            </div>
          </div>

          <div className="border border-border rounded-lg bg-card overflow-hidden">
            {loading ? (
              <div className="p-8 text-center text-muted-foreground text-sm">Chargement des événements de sécurité...</div>
            ) : filteredSecEvents.length === 0 ? (
              <div className="p-8 text-center text-muted-foreground text-sm">Aucun événement de sécurité trouvé.</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full border-collapse text-left text-sm">
                  <thead className="bg-muted text-muted-foreground text-xs uppercase font-medium">
                    <tr>
                      <th className="p-3">Horodatage</th>
                      <th className="p-3">Événement</th>
                      <th className="p-3">Acteur (Email)</th>
                      <th className="p-3">Classification</th>
                      <th className="p-3">Résultat</th>
                      <th className="p-3">Trace ID</th>
                      <th className="p-3 text-right">Détails</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {filteredSecEvents.map((evt) => (
                      <tr key={evt.id} className="hover:bg-muted/30">
                        <td className="p-3 font-mono text-xs whitespace-nowrap">
                          {new Date(evt.created_at).toLocaleString()}
                        </td>
                        <td className="p-3 font-semibold text-foreground">
                          {evt.audit_event}
                        </td>
                        <td className="p-3">
                          <div className="flex flex-col">
                            <span className="text-foreground">{evt.actor_email || 'Système'}</span>
                            <span className="text-xs text-muted-foreground">{evt.actor_role} · Clearance {evt.actor_clearance}</span>
                          </div>
                        </td>
                        <td className="p-3">
                          {evt.classification ? (
                            <span className={`px-2 py-0.5 rounded text-xs font-semibold ${
                              evt.classification === 'C4' ? 'bg-red-500/10 text-red-500' :
                              evt.classification === 'C3' ? 'bg-purple-500/10 text-purple-500' :
                              evt.classification === 'C2' ? 'bg-blue-500/10 text-blue-500' :
                              'bg-green-500/10 text-green-500'
                            }`}>
                              {evt.classification}
                            </span>
                          ) : '—'}
                        </td>
                        <td className="p-3">
                          <span className={`px-2 py-0.5 rounded text-xs font-semibold ${
                            evt.outcome === 'success' || evt.outcome === 'granted' || evt.outcome === 'allowed'
                              ? 'bg-green-500/10 text-green-500' 
                              : 'bg-red-500/10 text-red-500'
                          }`}>
                            {evt.outcome}
                          </span>
                        </td>
                        <td className="p-3 font-mono text-xs max-w-[120px] truncate">
                          {evt.trace_id ? (
                            <button
                              onClick={() => {
                                setSearchTraceId(evt.trace_id!);
                                setSearchedTraceId(evt.trace_id!);
                                setActiveTab('trace');
                              }}
                              className="text-primary hover:underline"
                            >
                              {evt.trace_id}
                            </button>
                          ) : '—'}
                        </td>
                        <td className="p-3 text-xs text-muted-foreground max-w-[200px] truncate text-right">
                          {evt.detail || 'Aucun détail'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Vue Onglet : Ledger Events */}
      {activeTab === 'ledger' && (
        <div className="border border-border rounded-lg bg-card overflow-hidden">
          {loading ? (
            <div className="p-8 text-center text-muted-foreground text-sm">Chargement du Ledger...</div>
          ) : ledgerEvents.length === 0 ? (
            <div className="p-8 text-center text-muted-foreground text-sm">Aucun événement dans le Ledger.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full border-collapse text-left text-sm">
                <thead className="bg-muted text-muted-foreground text-xs uppercase font-medium">
                  <tr>
                    <th className="p-3">Horodatage</th>
                    <th className="p-3">Type</th>
                    <th className="p-3">Dossier ID</th>
                    <th className="p-3">Acteur</th>
                    <th className="p-3">Statut</th>
                    <th className="p-3 text-right">Trace ID</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {ledgerEvents.map((evt) => (
                    <tr key={evt.event_id} className="hover:bg-muted/30">
                      <td className="p-3 font-mono text-xs whitespace-nowrap">
                        {new Date(evt.timestamp).toLocaleString()}
                      </td>
                      <td className="p-3 font-semibold text-foreground">
                        {evt.event_type}
                      </td>
                      <td className="p-3 font-mono text-xs">
                        {evt.dossier_id}
                      </td>
                      <td className="p-3">
                        {evt.actor ? `${evt.actor.email || 'Système'} (${evt.actor.role || '—'})` : 'Système'}
                      </td>
                      <td className="p-3">
                        <span className={`px-2 py-0.5 rounded text-xs font-semibold ${
                          evt.status === 'COMPLETED' ? 'bg-green-500/10 text-green-500' :
                          evt.status === 'HITL_WAITING' ? 'bg-orange-500/10 text-orange-500' :
                          'bg-muted text-muted-foreground'
                        }`}>
                          {evt.status}
                        </span>
                      </td>
                      <td className="p-3 font-mono text-xs text-right">
                        {evt.trace_id ? (
                          <button
                            onClick={() => {
                              setSearchTraceId(evt.trace_id!);
                              setSearchedTraceId(evt.trace_id!);
                              setActiveTab('trace');
                            }}
                            className="text-primary hover:underline"
                          >
                            {evt.trace_id.slice(0, 8)}...
                          </button>
                        ) : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Vue Onglet : Recherche Trace */}
      {activeTab === 'trace' && (
        <div className="space-y-6">
          <form onSubmit={handleSearchTrace} className="flex gap-2 max-w-xl">
            <input
              type="text"
              value={searchTraceId}
              onChange={(e) => setSearchTraceId(e.target.value)}
              placeholder="Saisir un trace_id (ex: UUID)..."
              className="flex-1 bg-background border border-border rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-primary"
            />
            <button
              type="submit"
              disabled={loading || !searchTraceId.trim()}
              className="bg-primary text-primary-foreground hover:bg-primary/95 px-4 py-2 rounded-md font-semibold text-sm disabled:opacity-50 transition-colors"
            >
              Rechercher
            </button>
          </form>

          {loading && (
            <div className="p-8 text-center text-muted-foreground text-sm">Recherche de la trace causale en cours...</div>
          )}

          {!loading && traceDetail && (
            <div className="space-y-6">
              <div className="bg-muted p-4 rounded-lg border border-border">
                <h3 className="font-semibold text-foreground">Détail du trace_id</h3>
                <p className="font-mono text-xs text-muted-foreground mt-1">{traceDetail.trace_id}</p>
                <p className="text-sm text-foreground mt-2">
                  Nombre total d'événements reliés de manière causale : <strong className="text-primary">{traceDetail.events.length}</strong>
                </p>
              </div>

              <div className="relative border-l-2 border-border ml-4 space-y-8 py-2">
                {traceDetail.events.map((evt, idx) => (
                  <div key={evt.event_id} className="relative pl-6">
                    {/* Icône chronologique */}
                    <div className="absolute -left-[9px] top-1 bg-background border-2 border-primary rounded-full p-0.5">
                      <div className="h-2 w-2 bg-primary rounded-full" />
                    </div>

                    <div className="bg-card border border-border rounded-lg p-4 space-y-2">
                      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-1">
                        <span className="font-bold text-foreground text-base">{evt.event_type}</span>
                        <span className="text-xs text-muted-foreground font-mono">
                          {new Date(evt.timestamp).toLocaleString()}
                        </span>
                      </div>

                      <div className="text-sm grid grid-cols-1 sm:grid-cols-2 gap-2 text-muted-foreground border-t border-border/50 pt-2">
                        <div>
                          <strong>Acteur :</strong> {evt.actor?.email || 'Système'} ({evt.actor?.role || '—'})
                        </div>
                        <div>
                          <strong>Dossier concerné :</strong> <span className="font-mono text-xs">{evt.dossier_id}</span>
                        </div>
                        {evt.duration_ms !== undefined && (
                          <div>
                            <strong>Durée :</strong> {evt.duration_ms} ms
                          </div>
                        )}
                        {evt.cost !== undefined && (
                          <div>
                            <strong>Coût :</strong> {evt.cost.toFixed(4)} €
                          </div>
                        )}
                      </div>

                      {evt.payload && Object.keys(evt.payload).length > 0 && (
                        <div className="bg-muted/50 p-2 rounded text-xs font-mono text-foreground overflow-x-auto max-h-48 mt-2">
                          <pre>{JSON.stringify(evt.payload, null, 2)}</pre>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
